import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calculator, ArrowLeft, DollarSign, Package, Truck, Percent, Tag } from 'lucide-react';

// Shipping charges data (Updated with new rates and countries)
const shippingCharges = [
  { "weight": 50, "USA": 546.34, "Canada": 744.58, "Australia": 613.6, "UK": 405.92 },
  { "weight": 100, "USA": 584.1, "Canada": 779.98, "Australia": 640.74, "UK": 435.42 },
  { "weight": 150, "USA": 757.56, "Canada": 814.2, "Australia": 669.06, "UK": 463.74 },
  { "weight": 200, "USA": 804.76, "Canada": 848.42, "Australia": 698.56, "UK": 493.24 },
  { "weight": 250, "USA": 932.2, "Canada": 882.64, "Australia": 725.7, "UK": 522.74 },
  { "weight": 300, "USA": 991.2, "Canada": 916.86, "Australia": 754.02, "UK": 551.06 },
  { "weight": 350, "USA": 1064.36, "Canada": 951.08, "Australia": 781.16, "UK": 580.56 },
  { "weight": 400, "USA": 1119.82, "Canada": 1030.14, "Australia": 810.66, "UK": 610.06 },
  { "weight": 450, "USA": 1262.6, "Canada": 1065.54, "Australia": 838.98, "UK": 638.38 },
  { "weight": 500, "USA": 1436.06, "Canada": 1099.76, "Australia": 866.12, "UK": 667.88 },
  { "weight": 600, "USA": 1551.7, "Canada": 1168.2, "Australia": 964.06, "UK": 725.7 },
  { "weight": 700, "USA": 1666.16, "Canada": 1358.18, "Australia": 1021.88, "UK": 784.7 },
  { "weight": 800, "USA": 1781.8, "Canada": 1583.56, "Australia": 1077.34, "UK": 842.52 },
  { "weight": 900, "USA": 1896.2, "Canada": 1652, "Australia": 1135.16, "UK": 900.34 },
  { "weight": 1000, "USA": 2096.86, "Canada": 1720.44, "Australia": 1190.62, "UK": 959.34 },
  { "weight": 1100, "USA": 2276.22, "Canada": 2021.34, "Australia": 1293.28, "UK": 1017.16 },
  { "weight": 1200, "USA": 2397.76, "Canada": 2089.78, "Australia": 1348.74, "UK": 1074.98 },
  { "weight": 1300, "USA": 2518.12, "Canada": 2159.41, "Australia": 1406.56, "UK": 1133.98 },
  { "weight": 1400, "USA": 2715.18, "Canada": 2227.84, "Australia": 1462.02, "UK": 1191.82 },
  { "weight": 1500, "USA": 2836.72, "Canada": 2217.22, "Australia": 1518.66, "UK": 1249.62 },
  { "weight": 1600, "USA": 2957.08, "Canada": 2296.28, "Australia": 1574.12, "UK": 1308.62 },
  { "weight": 1700, "USA": 3078.62, "Canada": 2376.52, "Australia": 1631.94, "UK": 1366.44 },
  { "weight": 1800, "USA": 3198.98, "Canada": 2455.58, "Australia": 1687.4, "UK": 1424.26 },
  { "weight": 1900, "USA": 3406.66, "Canada": 2621.96, "Australia": 1744.04, "UK": 1483.26 },
  { "weight": 2000, "USA": 3527.02, "Canada": 2701.02, "Australia": 1799.5, "UK": 1541.08 },
  { "weight": 2500, "USA": 4224.4, "Canada": 3191.92, "Australia": 2108.66, "UK": 1832.54 },
  { "weight": 3000, "USA": 4898.18, "Canada": 3615.52, "Australia": 2389.5, "UK": 2122.82 },
  { "weight": 3500, "USA": 5576.68, "Canada": 4012, "Australia": 2738.78, "UK": 3135.26 },
  { "weight": 4000, "USA": 6231.58, "Canada": 4420.28, "Australia": 3019.62, "UK": 3482.18 },
  { "weight": 4500, "USA": 6901.82, "Canada": 5043.32, "Australia": 3331.14, "UK": 3827.92 },
  { "weight": 5000, "USA": 7590.94, "Canada": 5525.94, "Australia": 3614.34, "UK": 4174.84 },
  { "weight": 6000, "USA": 8580.96, "Canada": 3958.94, "Australia": 4055.66, "UK": 3370 },
  { "weight": 9000, "USA": 9863.62, "Canada": 4481.64, "Australia": 4730.62, "UK": 3480 },
  { "weight": 11000, "USA": 11146.28, "Canada": 5167.22, "Australia": 5406.76, "UK": 3590 },
  { "weight": 12000, "USA": 12428.94, "Canada": 5688.78, "Australia": 5620.34, "UK": 3610 },
  { "weight": 13000, "USA": 13711.6, "Canada": 6209.16, "Australia": 6245.74, "UK": 0 }
];

// Exchange rates (Real-time rates from BookMyForex - Updated every few seconds)
const exchangeRates = {
  USD: 0.011335, // 1 INR = 0.011335 USD (≈ 88.22 INR per USD)
  AUD: 0.017407, // 1 INR = 0.017407 AUD (≈ 57.45 INR per AUD)
  CAD: 0.015677, // 1 INR = 0.015677 CAD (≈ 63.78 INR per CAD)
  GBP: 0.009012  // 1 INR = 0.009012 GBP (≈ 110.96 INR per GBP)
};



interface CalculationResult {
  productPrice: number;
  shippingCost: number;
  totalINR: number;
  referralFee: number;
  baseCostINR: number;
  // Final Price (with fixed profit + referral fee)
  finalPriceINR: number;
  finalPriceUSD: number;
  finalPriceAUD: number;
  finalPriceCAD: number;
  finalPriceGBP: number;
  // Sale Price (10% discount from Final Price)
  salePriceINR: number;
  salePriceUSD: number;
  salePriceAUD: number;
  salePriceCAD: number;
  salePriceGBP: number;
  country: string;
  profitMargin: number;
  saleDiscount: number;
}

const ListingPriceCalculator: React.FC = () => {
  const [productPrice, setProductPrice] = useState<string>('');
  const [weight, setWeight] = useState<string>('');
  const [country, setCountry] = useState<string>('USA');
  const [result, setResult] = useState<CalculationResult | null>(null);

  const findNearestWeight = (inputWeight: number): number => {
    // Find the nearest weight bracket (round up to next available weight)
    const sortedWeights = shippingCharges.map(item => item.weight).sort((a, b) => a - b);

    for (const availableWeight of sortedWeights) {
      if (inputWeight <= availableWeight) {
        return availableWeight;
      }
    }

    // If weight exceeds maximum, use the highest bracket
    return sortedWeights[sortedWeights.length - 1];
  };

  const calculatePrice = () => {
    const priceNum = parseFloat(productPrice);
    const weightNum = parseFloat(weight);

    if (isNaN(priceNum) || isNaN(weightNum) || priceNum <= 0 || weightNum <= 0) {
      return;
    }

    // Find nearest weight and get shipping cost
    const nearestWeight = findNearestWeight(weightNum);
    const shippingData = shippingCharges.find(item => item.weight === nearestWeight);

    if (!shippingData) return;

    const shippingCost = shippingData[country as keyof typeof shippingData];

    // Calculate base cost (product + shipping only)
    const totalINR = priceNum + shippingCost;
    const baseCostINR = totalINR; // Base cost without referral fee

    // Simplified pricing structure
    const fixedProfit = 1000; // Fixed 1000 INR profit
    const saleDiscount = 0.10; // 10% sale discount

    // Helper function for rounding
    const round2 = (num: number) => Math.round(num * 100) / 100;

    // 1. Calculate 25% referral fee on base price
    const referralFee = baseCostINR * 0.25; // 25% referral fee on base price

    // 2. Final Price = Base Cost + Fixed Profit + Referral Fee
    const finalPriceINR = round2(baseCostINR + fixedProfit + referralFee);
    const finalPriceUSD = round2(finalPriceINR * exchangeRates.USD);
    const finalPriceAUD = round2(finalPriceINR * exchangeRates.AUD);
    const finalPriceCAD = round2(finalPriceINR * exchangeRates.CAD);
    const finalPriceGBP = round2(finalPriceINR * exchangeRates.GBP);

    // 4. Sale Price = Final Price - 10% discount
    const salePriceINR = round2(finalPriceINR * (1 - saleDiscount));
    const salePriceUSD = round2(salePriceINR * exchangeRates.USD);
    const salePriceAUD = round2(salePriceINR * exchangeRates.AUD);
    const salePriceCAD = round2(salePriceINR * exchangeRates.CAD);
    const salePriceGBP = round2(salePriceINR * exchangeRates.GBP);

    setResult({
      productPrice: priceNum,
      shippingCost,
      totalINR,
      referralFee,
      baseCostINR,
      // Final Price (with profit margin)
      finalPriceINR,
      finalPriceUSD,
      finalPriceAUD,
      finalPriceCAD,
      finalPriceGBP,
      // Sale Price (with discount)
      salePriceINR,
      salePriceUSD,
      salePriceAUD,
      salePriceCAD,
      salePriceGBP,
      country,
      profitMargin: fixedProfit, // Fixed profit amount in INR
      saleDiscount: saleDiscount * 100, // Convert to percentage for display
    });
  };

  useEffect(() => {
    if (productPrice && weight) {
      calculatePrice();
    }
  }, [productPrice, weight, country]);

  const formatCurrency = (amount: number, currency: string = 'INR') => {
    const symbols = {
      INR: '₹',
      USD: '$',
      AUD: 'A$',
      CAD: 'C$',
      GBP: '£'
    };

    return `${symbols[currency as keyof typeof symbols]}${amount.toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center mb-6">
            <motion.div
              className="p-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl shadow-lg"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <Calculator className="w-8 h-8 text-white" />
            </motion.div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
            Listing Price Calculator
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Calculate your final listing prices for international marketplaces including shipping and fees
          </p>
        </motion.div>

        {/* Back to Home Button */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Card */}
          <motion.div
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/30 p-8"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white flex items-center">
              <Package className="w-6 h-6 mr-3 text-blue-500" />
              Product Details
            </h2>

            <div className="space-y-6">
              {/* Product Price Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Original Product Price (INR)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="number"
                    value={productPrice}
                    onChange={(e) => setProductPrice(e.target.value)}
                    placeholder="Enter price in INR"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                  />
                </div>
              </div>

              {/* Weight Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Product Weight (grams)
                </label>
                <div className="relative">
                  <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="Enter weight in grams"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                  />
                </div>
              </div>

              {/* Country Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Destination Country
                </label>
                <div className="relative">
                  <Truck className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                  >
                    <option value="USA">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                    <option value="UK">United Kingdom</option>
                  </select>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Results Card */}
          <motion.div
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/30 p-8"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white flex items-center">
              <Calculator className="w-6 h-6 mr-3 text-green-500" />
              Price Breakdown
            </h2>

            {result ? (
              <div className="space-y-4">
                {/* Breakdown Table */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">Product Price:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(result.productPrice)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400 flex items-center">
                      <Truck className="w-4 h-4 mr-1" />
                      Shipping ({result.country}):
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(result.shippingCost)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400 flex items-center">
                      <Percent className="w-4 h-4 mr-1" />
                      Referral Fee (15%):
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(result.referralFee)}
                    </span>
                  </div>

                  <hr className="border-gray-300 dark:border-gray-600" />

                  <div className="flex justify-between items-center text-lg">
                    <span className="font-bold text-gray-900 dark:text-white">Base Cost (INR):</span>
                    <span className="font-bold text-red-600 dark:text-red-400">
                      {formatCurrency(result.baseCostINR)}
                    </span>
                  </div>
                </div>

                {/* Final Price (with profit margin) */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center">
                    <DollarSign className="w-5 h-5 mr-2 text-green-500" />
                    Final Price (+₹{result.profitMargin} Fixed Profit):
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400 font-semibold">INR (India):</span>
                      <span className="font-bold text-green-600 dark:text-green-400 text-xl">
                        {formatCurrency(result.finalPriceINR)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">USD (United States):</span>
                      <span className="font-bold text-blue-600 dark:text-blue-400">
                        {formatCurrency(result.finalPriceUSD, 'USD')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">AUD (Australia):</span>
                      <span className="font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(result.finalPriceAUD, 'AUD')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">CAD (Canada):</span>
                      <span className="font-bold text-purple-600 dark:text-purple-400">
                        {formatCurrency(result.finalPriceCAD, 'CAD')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">GBP (United Kingdom):</span>
                      <span className="font-bold text-indigo-600 dark:text-indigo-400">
                        {formatCurrency(result.finalPriceGBP, 'GBP')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Sale Price (with discount) */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center">
                    <Tag className="w-5 h-5 mr-2 text-orange-500" />
                    Sale Price ({result.saleDiscount}% Discount):
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400 font-semibold">INR (India):</span>
                      <span className="font-bold text-orange-600 dark:text-orange-400 text-xl">
                        {formatCurrency(result.salePriceINR)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">USD (United States):</span>
                      <span className="font-bold text-blue-600 dark:text-blue-400">
                        {formatCurrency(result.salePriceUSD, 'USD')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">AUD (Australia):</span>
                      <span className="font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(result.salePriceAUD, 'AUD')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">CAD (Canada):</span>
                      <span className="font-bold text-purple-600 dark:text-purple-400">
                        {formatCurrency(result.salePriceCAD, 'CAD')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">GBP (United Kingdom):</span>
                      <span className="font-bold text-indigo-600 dark:text-indigo-400">
                        {formatCurrency(result.salePriceGBP, 'GBP')}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-gray-600 dark:text-gray-400 bg-white/50 dark:bg-gray-800/50 rounded-lg p-2">
                    <p className="font-semibold">You Save: {formatCurrency(result.finalPriceINR - result.salePriceINR)} INR</p>
                  </div>
                </div>

                {/* Pricing Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center">
                    <Calculator className="w-5 h-5 mr-2 text-blue-500" />
                    Pricing Summary:
                  </h3>
                  <div className="space-y-3">
                    <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Base Cost:</p>
                          <p className="font-bold text-gray-900 dark:text-white">{formatCurrency(result.baseCostINR)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Fixed Profit:</p>
                          <p className="font-bold text-green-600 dark:text-green-400">₹{result.profitMargin}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Final Price Profit:</p>
                          <p className="font-bold text-green-600 dark:text-green-400">
                            {formatCurrency(result.finalPriceINR - result.baseCostINR)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Sale Price Profit:</p>
                          <p className="font-bold text-orange-600 dark:text-orange-400">
                            {formatCurrency(result.salePriceINR - result.baseCostINR)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/30 rounded-lg p-2">
                    <p className="font-semibold">Simplified Pricing Strategy:</p>
                    <p>• <strong>Base Cost:</strong> Product price + shipping</p>
                    <p>• <strong>Final Price:</strong> Base Cost + ₹{result.profitMargin} profit + 25% referral fee (on base cost)</p>
                    <p>• <strong>Sale Price:</strong> Final Price - {result.saleDiscount}% discount</p>
                    <p>• Both prices shown in all currencies (INR, USD, AUD, CAD, GBP)</p>
                  </div>
                </div>

                {/* Exchange Rate Info */}
                <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/30 rounded-lg p-3">
                  <p className="font-semibold mb-1">Exchange Rates Used (Live BookMyForex Rates):</p>
                  <p>1 INR = $0.011335 USD | A$0.017407 AUD | C$0.015677 CAD | £0.009012 GBP</p>
                  <p className="mt-1 text-xs opacity-75">Live rates from BookMyForex - Updated every few seconds (≈ 88.22 INR/USD, 57.45 INR/AUD, 63.78 INR/CAD, 110.96 INR/GBP)</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Calculator className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  Enter product price and weight to see the calculation
                </p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Exchange Rate Details */}
        <motion.div
          className="mt-8 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/30 p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Current Exchange Rates</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
              <h4 className="font-semibold text-blue-600 dark:text-blue-400 mb-2">USD (US Dollar)</h4>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">₹{(1 / exchangeRates.USD).toFixed(2)}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">per 1 USD</p>
            </div>
            <div className="text-center bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
              <h4 className="font-semibold text-green-600 dark:text-green-400 mb-2">AUD (Australian Dollar)</h4>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">₹{(1 / exchangeRates.AUD).toFixed(2)}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">per 1 AUD</p>
            </div>
            <div className="text-center bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
              <h4 className="font-semibold text-purple-600 dark:text-purple-400 mb-2">CAD (Canadian Dollar)</h4>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">₹{(1 / exchangeRates.CAD).toFixed(2)}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">per 1 CAD</p>
            </div>
            <div className="text-center bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4">
              <h4 className="font-semibold text-indigo-600 dark:text-indigo-400 mb-2">GBP (British Pound)</h4>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">₹{(1 / exchangeRates.GBP).toFixed(2)}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">per 1 GBP</p>
            </div>
          </div>
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              * Live rates from BookMyForex updated every few seconds. Rates may fluctuate in real-time.
            </p>
          </div>
        </motion.div>

        {/* Additional Info */}
        <motion.div
          className="mt-8 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/30 p-8"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-blue-600 dark:text-blue-400 font-bold">1</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Calculate Base Cost</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Product price + shipping cost only
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-green-600 dark:text-green-400 font-bold">2</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Add Profit & Referral Fee</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Base cost + ₹1000 + 25% referral fee = Final Price
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-orange-600 dark:text-orange-400 font-bold">3</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Calculate Sale Price</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Final price - 10% discount = Sale Price
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-purple-600 dark:text-purple-400 font-bold">4</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Multi-Currency Display</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                All prices shown in INR, USD, AUD, CAD, GBP
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ListingPriceCalculator;
